import './login.scss';

import type { PageProps } from 'keycloakify/login/pages/PageProps';
import { useState } from 'react';

import { i18n as fallbackI18n } from '../i18n';
import type { KcContext } from '../kcContext';
import Template from '../Template';

export default function LoginConfigTotp(
  props: Omit<
    PageProps<Extract<KcContext, { pageId: 'login-config-totp.ftl' }>, any>,
    'Template' | 'doUseDefaultCss'
  >
) {
  const { kcContext, i18n } = props;

  const { url, isAppInitiatedAction, totp, mode, messagesPerField } = kcContext;
  const { msg, msgStr } = i18n ?? fallbackI18n;

  const algToKeyUriAlg = {
    HmacSHA1: 'SHA1',
    HmacSHA256: 'SHA256',
    HmacSHA512: 'SHA512',
  };

  const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const disableSubmitButton = () => {
    setSubmitButtonDisabled(true);
  };

  return (
    <Template
      kcContext={kcContext}
      i18n={i18n}
      doUseDefaultCss={true}
      headerNode={msg('loginTotpTitle')}
    >
      <>
        <h1>Authenticatie-app instellen</h1>
        <p>
          Je logt bij GGZ Ecademy extra veilig in. Eerst met je inlognaam en wachtwoord. En dan met
          een code. Deze code krijg je van een authenticatie-app. Je regelt dit in 3 stappen:
          <br />
          <ol id="kc-totp-settings">
            <li>
              <p>
                Installeer een Authenticator app op je mobiele telefoon of browser.
                <ul></ul>
              </p>
            </li>

            {mode && mode == 'manual' ? (
              <>
                <li>
                  <p>Vul de code uit je authenticatie-app in:</p>
                  <p>
                    <span id="kc-totp-secret-key">{totp.totpSecretEncoded}</span>
                  </p>
                  <p>
                    <a href={totp.qrUrl} id="mode-barcode">
                      {msg('loginTotpScanBarcode')}
                    </a>
                  </p>
                </li>
                <li>
                  <p>{msg('loginTotpManualStep3')}</p>
                  <p>
                    <ul>
                      <li id="kc-totp-type">
                        {msg('loginTotpType')}: {msg(`loginTotp.${totp.policy.type}`)}
                      </li>
                      <li id="kc-totp-algorithm">
                        {msg('loginTotpAlgorithm')}:{' '}
                        {algToKeyUriAlg?.[totp.policy.algorithm] ?? totp.policy.algorithm}
                      </li>
                      <li id="kc-totp-digits">
                        {msg('loginTotpDigits')}: {totp.policy.digits}
                      </li>
                      {totp.policy.type === 'totp' ? (
                        <li id="kc-totp-period">
                          {msg('loginTotpInterval')}: {totp.policy.period}
                        </li>
                      ) : (
                        <li id="kc-totp-counter">
                          {msg('loginTotpCounter')}: {totp.policy.initialCounter}
                        </li>
                      )}
                    </ul>
                  </p>
                </li>
              </>
            ) : (
              <li>
                <p>Scan deze QR-code met de app:</p>
                <img
                  id="kc-totp-secret-qr-code"
                  src={`data:image/png;base64, ${totp.totpSecretQrCode}`}
                  alt="Figure: Barcode"
                />

                {/*<br />*/}
                {/*<p>*/}
                {/*  <a href={totp.manualUrl} id="mode-manual">*/}
                {/*    {msg('loginTotpUnableToScan')}*/}
                {/*  </a>*/}
                {/*</p>*/}
              </li>
            )}
            <li>
              <p>Vul de code in uit de authenticatie-app op je mobiele telefoon of je browser:</p>

              {/*<p>{msg('loginTotpStep3DeviceName')}</p>*/}
            </li>
          </ol>
        </p>

        <form
          onSubmit={disableSubmitButton}
          action={url.loginAction}
          className="kcFormClass"
          id="kc-totp-settings-form"
          method="post"
        >
          <div className="kcFormGroupClass">
            <div className="kcInputWrapperClass" style={{ marginTop: 0 }}>
              <input
                type="text"
                id="totp"
                name="totp"
                autoComplete="off"
                className="kcInputClass"
                aria-invalid={messagesPerField.existsError('totp')}
              />
              {messagesPerField.existsError('totp') && (
                <span
                  id="input-error-otp-code"
                  className="kcInputErrorMessageClass"
                  aria-live="polite"
                >
                  {messagesPerField.get('totp')}
                </span>
              )}
            </div>
            <input type="hidden" id="totpSecret" name="totpSecret" value={totp.totpSecret} />
            {mode && <input type="hidden" id="mode" value={mode} />}
          </div>

          {isAppInitiatedAction ? (
            <>
              <input
                type="submit"
                className={`btn btn-primary ${isSubmitButtonDisabled ? 'loading' : ''}`}
                disabled={isSubmitButtonDisabled}
                id="saveTOTPBtn"
                value={msgStr('doSubmit')}
              />
              <button
                type="submit"
                id="cancelTOTPBtn"
                className="btn btn-secondary"
                name="cancel-aia"
                value="true"
              >
                Annuleer
              </button>
            </>
          ) : (
            <div>
              <button
                type="submit"
                className={`btn btn-primary ${isSubmitButtonDisabled ? 'loading' : ''}`}
                disabled={isSubmitButtonDisabled}
                id="saveTOTPBtn"
              >
                {msgStr('doSubmit')}
              </button>
              <p className="privacy-text">
                Klopt de actievatiecode niet? Scan de QR-code voor een nieuwe activatiecode. <br />{' '}
                Meer weten of kom je er niet uit? Lees de{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://support.ggzecademy.nl/support/solutions/articles/23000025621-2-factor-authenticatie-2fa-/ "
                >
                  uitgebreide uitleg
                </a>{' '}
                over 2-Factor-Authenticatie op onze support-site.
              </p>
            </div>
          )}
        </form>
      </>
    </Template>
  );
}
